import { Input } from '../../Inputs/Input';
import { Radio } from '../../Radio';
import { Button } from '../../Button';
import { formRules } from '../FormRules';
import { CheckboxesTags, Option } from '../../Inputs/Checkboxes';
import { apiService } from '../../../services/api';
import { FormProvider, useForm } from 'react-hook-form';
import { ChangeEvent, useEffect, useState } from 'react';
import { useAppSelector, useToast } from '../../../hooks';
import {
  removeNonNumericChars,
  formatCpf,
  formatCnpj,
  formatPhone,
  formatStringMoney,
  readMoney,
  formatMoney,
  formatBankDisplayname,
  formatBankId,
} from '../../../utils/formatters';
import { Notification } from '../NotificationComponent';
import { makeLevelLabel } from '../../CalendarEvent';
import { Add } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { CreateSubject } from './CreateSubject';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Datepicker } from '../../Datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { BankBrasilAPIRes } from '../../../services/brasilAPI.types';
import { brasilAPIService } from '../../../services/brasilAPI';
import { InputAutocomplete } from '../../Inputs/Autocomplete';
import { DataAutocomplete } from '../../SelectResponsibles';
import { UsersFormProps } from '../../../pages/Register/form-props.types';
import { UpdateUserReq, UserRole } from '../../../services/api.types';
import { usersService } from '../../../services/users.service';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

export interface TeacherFormValues {
  fullName: string;
  displayName: string;
  email: string;
  informationType: 'cpf' | 'cnpj';
  cpf: string;
  cnpj: string;
  phone: string;
  birthdate: Dayjs | null;
  pix: string;
  bank: DataAutocomplete | null;
  account: string;
  agency: string;
  classElementaryValue: string;
  classUniversityValue: string;
  notification: {
    notifyEmail: boolean;
    //notifySMS: boolean;
    notifyWhatsapp: boolean;
  };
  selectedSubjects: Option[];
  active?: boolean;
  role?: UserRole;
}

export function TeacherRegisterForm({
  mode,
  userToUpdate,
}: Readonly<UsersFormProps>) {
  const toast = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [banks, setBanks] = useState<BankBrasilAPIRes[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const user = useAppSelector((state) => state.user);
  const selfEdit = userToUpdate?.id === user?.id;
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;

  const getTeacherDefaultValues = (): TeacherFormValues => {
    if (userToUpdate && mode === 'UPDATE') {
      return {
        displayName: userToUpdate.displayName,
        fullName: userToUpdate.fullName,
        email: userToUpdate.email,
        active: userToUpdate.active,
        role: userToUpdate.role,
        informationType: userToUpdate.teacher.cpf ? 'cpf' : 'cnpj',
        cpf: userToUpdate.teacher?.cpf
          ? formatCpf(userToUpdate.teacher?.cpf)
          : '',
        cnpj: userToUpdate.teacher?.cnpj
          ? formatCnpj(userToUpdate.teacher?.cnpj)
          : '',
        phone: formatPhone(userToUpdate.teacher.phone),
        notification: {
          notifyEmail: userToUpdate.notifyEmail,
          notifyWhatsapp: userToUpdate.notifyWhatsapp,
        },
        birthdate: dayjs(userToUpdate.teacher.birthdate),
        bank: null,
        account: userToUpdate.teacher.account,
        agency: userToUpdate.teacher.agency,
        pix: userToUpdate.teacher.pix,
        classElementaryValue: formatMoney(
          userToUpdate.teacher.classElementaryValue
        ),
        classUniversityValue: formatMoney(
          userToUpdate.teacher.classUniversityValue
        ),
        selectedSubjects: [],
      };
    }

    return {
      fullName: '',
      displayName: '',
      email: '',
      informationType: 'cpf',
      cpf: '',
      cnpj: '',
      phone: '',
      birthdate: null,
      pix: '',
      bank: null,
      account: '',
      agency: '',
      classElementaryValue: formatMoney(0),
      classUniversityValue: formatMoney(0),
      notification: {
        notifyEmail: true,
        notifyWhatsapp: true,
      },
      selectedSubjects: [],
    };
  };

  const forms = useForm<TeacherFormValues>({
    mode: 'all',
    defaultValues: getTeacherDefaultValues(),
  });

  useEffect(() => {
    if (userToUpdate) {
      const fetchBankDisplayName = async () => {
        return await brasilAPIService.getBankByCode(userToUpdate.teacher.bank);
      };
      fetchBankDisplayName().then((res) => {
        forms.setValue('bank', {
          id: Number(userToUpdate.teacher.bank),
          displayName: formatBankDisplayname(res),
        });
      });
      const subjectsTaught = userToUpdate.teacher.subjectsTaught
        .filter((id) => options.some((subject) => subject.id === id))
        .map((id) => ({
          id,
          label: options.find((subject) => subject.id === id)?.label ?? '',
        }));
      forms.setValue('selectedSubjects', subjectsTaught);
    }
  }, [userToUpdate, forms, options]);

  const informationType = forms.watch('informationType');
  const informationTypeRules =
    informationType === 'cpf' ? formRules.cpfRequired : formRules.cnpjRequired;
  const handleMaskInformationType = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (informationType === 'cpf') handleChangeMaskCPF(event);
    if (informationType === 'cnpj') handleChangeMaskCNPJ(event);
  };

  useEffect(() => {
    apiService.getAllSubjects().then((response) => {
      setOptions(
        response.map((subject) => ({
          id: subject.id,
          label: makeLevelLabel[subject.level](subject.name),
        }))
      );
    });
  }, []);

  useEffect(() => {
    brasilAPIService.getAllBanks().then((response: BankBrasilAPIRes[]) => {
      const banks = response
        .filter((bank) => bank.code)
        .sort((a, b) => a.code - b.code);
      setBanks(banks);
    });
  }, []);
  const fullName = forms.watch('fullName');
  useEffect(() => {
    if (mode === 'CREATE') {
      forms.setValue('displayName', fullName);
    }
  }, [fullName, mode, forms]);

  const onSubmit = async (data: TeacherFormValues) => {
    const {
      informationType,
      cpf,
      cnpj,
      classElementaryValue,
      classUniversityValue,
      phone,
      fullName,
      notification,
      birthdate,
      selectedSubjects,
      bank,
      ...newData
    } = data;
    const subjectIdsTaught = selectedSubjects.map((subject) => subject.id);

    const updateAPImethod = (data: UpdateUserReq) =>
      userToUpdate && userToUpdate.id !== user?.id
        ? usersService.updateUserById(userToUpdate.id, data)
        : usersService.updateSelfUser(data);

    if (mode === 'UPDATE') {
      setIsLoading(true);
      await updateAPImethod({
        displayName: newData.displayName,
        fullName: isAdminOrEmployee ? fullName : undefined,
        email: isAdminOrEmployee ? newData.email : undefined,
        ...notification,
        role: newData.role,
        active: isAdminOrEmployee ? newData.active : undefined,
        [UserRole.TEACHER]: {
          phone: isAdminOrEmployee ? removeNonNumericChars(phone) : undefined,
          account: isAdminOrEmployee
            ? removeNonNumericChars(newData.account)
            : undefined,
          agency: isAdminOrEmployee
            ? removeNonNumericChars(newData.agency)
            : undefined,
          pix: isAdminOrEmployee ? newData.pix : undefined,
          bank:
            bank?.id && isAdminOrEmployee ? formatBankId(bank.id) : undefined,
          classElementaryValue: isAdminOrEmployee
            ? readMoney(classElementaryValue)
            : undefined,
          classUniversityValue: isAdminOrEmployee
            ? readMoney(classUniversityValue)
            : undefined,
          birthdate: isAdminOrEmployee ? birthdate?.toDate() : undefined,
          cpf: undefined,
          cnpj: undefined,
          subjectsTaught: isAdminOrEmployee
            ? subjectIdsTaught.map((id) => Number(id))
            : undefined,
        },
      })
        .then(() => {
          toast.success('Professor alterado!');
          navigate(-1);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    if (mode === 'CREATE') {
      if (!bank) {
        toast.error('Selecione um banco');
        return;
      }

      if (!birthdate) {
        toast.error('Preencha a data de nascimento');
        return;
      }

      setIsLoading(true);
      await apiService
        .registerTeacher({
          ...newData,
          ...notification,
          ...(informationType === 'cpf'
            ? { cpf: removeNonNumericChars(cpf) }
            : { cnpj: removeNonNumericChars(cnpj) }),
          birthdate: birthdate.toDate(),
          fullName: fullName,
          phone: removeNonNumericChars(phone),
          classElementaryValue: readMoney(classElementaryValue),
          classUniversityValue: readMoney(classUniversityValue),
          subjectIdsTaught: subjectIdsTaught.map((id) => Number(id)),
          bank: formatBankId(bank.id),
        })
        .then(() => {
          forms.reset(getTeacherDefaultValues());
          toast.success('Professor cadastrado!');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleChangeMaskCPF = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('cpf', formatCpf(event.target.value));
  };

  const handleChangeMaskCNPJ = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('cnpj', formatCnpj(event.target.value));
  };

  const handleChangeMaskPhone = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('phone', formatPhone(event.target.value));
  };

  const handleChangeMaskMoney = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: keyof TeacherFormValues
  ) => {
    forms.setValue(field, formatStringMoney(event.target.value));
  };

  return (
    <>
      <FormProvider {...forms}>
        <form
          name="teacher-form"
          onSubmit={forms.handleSubmit(onSubmit)}
          className="flex flex-col items-center gap-2 w-full box-border pt-2 p-4 justify-between"
        >
          <Input
            disabled={selfEdit}
            name="fullName"
            label="Nome Completo"
            rules={formRules.fullNameRequired}
          />
          <Input name="displayName" label="Como gostaria de ser chamado(a)?" />
          <Input
            disabled={selfEdit}
            name="email"
            type="email"
            label="Email"
            rules={formRules.emailRequired}
          />
          <Radio
            name="informationType"
            options={[
              { value: 'cpf', label: 'CPF' },
              { value: 'cnpj', label: 'CNPJ' },
            ]}
            direction="row"
            disabled={mode === 'UPDATE'}
          />
          <Input
            name={informationType}
            label={informationType === 'cpf' ? 'CPF' : 'CNPJ'}
            onChange={handleMaskInformationType}
            rules={informationTypeRules}
            disabled={mode === 'UPDATE'}
          />
          <Input
            name="phone"
            label="Telefone"
            rules={formRules.phoneRequired}
            onChange={handleChangeMaskPhone}
            disabled={selfEdit}
          />

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
          >
            <Datepicker
              disabled={selfEdit}
              className="w-full"
              labelOut
              name="birthdate"
              label="Data de Nascimento"
              rules={formRules.birthdateRequired}
            />
          </LocalizationProvider>

          <Input name="pix" label="Chave PIX" disabled={selfEdit} />

          <InputAutocomplete
            name="bank"
            label="Banco"
            placeholder="Selecione ou digite o nome ou código do banco"
            rules={formRules.bankRequired}
            CustomOnChange={(_, value) => {
              forms.setValue('bank', value);
            }}
            values={banks.map((bank) => ({
              id: bank.code,
              displayName: formatBankDisplayname(bank),
            }))}
            disabled={selfEdit}
          />

          <Input
            name="agency"
            rules={formRules.agencyRequired}
            label="Agência"
            disabled={selfEdit}
          />

          <Input
            name="account"
            rules={formRules.accountRequired}
            label="Conta"
            disabled={selfEdit}
          />

          <Input
            disabled={selfEdit}
            name="classElementaryValue"
            rules={formRules.elementaryRequired}
            label="Valor da Hora/Aula do Ensino Fundamental / Médio / Técnico"
            onChange={(e) => handleChangeMaskMoney(e, 'classElementaryValue')}
          />

          <Input
            disabled={selfEdit}
            name="classUniversityValue"
            rules={formRules.universityRequired}
            label="Valor da Hora/Aula do Ensino Superior"
            onChange={(e) => handleChangeMaskMoney(e, 'classUniversityValue')}
          />

          <div className="w-full flex gap-4">
            <div className="w-full shrink">
              <CheckboxesTags
                name="selectedSubjects"
                label="Matérias Lecionadas"
                options={options}
                rules={formRules.subjectsRequired}
                disabled={selfEdit}
              />
            </div>
            <IconButton
              className={clsx(
                'h-min mt-7',
                'bg-orange-primary hover:bg-orange-primary hover:brightness-75'
              )}
              disabled={selfEdit}
              onClick={() => setOpen(true)}
            >
              <Add />
            </IconButton>
          </div>

          <Notification
            label="Notificações"
            name="notification"
            disableEmailField={mode === 'CREATE'}
          />
          <Button type="submit" isLoading={isLoading} circular>
            {mode === 'CREATE' && 'Cadastrar'}
            {mode === 'UPDATE' && 'Alterar'}
          </Button>
        </form>
      </FormProvider>
      {open && (
        <CreateSubject
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          onCreateSubject={(res) => {
            const newSubjects = res.map((subject) => ({
              id: subject.id,
              label: makeLevelLabel[subject.level](subject.name),
            }));
            setOptions([...options, ...newSubjects]);
          }}
        />
      )}
    </>
  );
}
